// src/services/permissions/equityPermissionService.js
// Equity Manager-specific permission service

import { supabase } from '../../config';
import { ROLES } from '../constants/roleConstants';
import { checkSuperAdmin } from './adminPermissionService';

/**
 * Get user's role for a specific company
 * @param {string} userId - User ID
 * @param {string} companyId - Company ID
 * @returns {Promise<string|null>} User's role or null
 */
export const getEquityRole = async (userId, companyId) => {
    try {
        const isSuperAdmin = await checkSuperAdmin(userId);
        if (isSuperAdmin) return ROLES.SUPER_ADMIN;

        const { data, error } = await supabase
            .from('equity_users')
            .select('role')
            .eq('user_id', userId)
            .eq('company_id', companyId)
            .single();
        
        if (error) {
            if (error.code === 'PGRST116') return null;
            throw error;
        }
        
        return data?.role || null;
    } catch (error) {
        console.error('Error getting equity role:', error);
        return null;
    }
};

/**
 * Get all companies for a user
 * @param {string} userId - User ID
 * @returns {Promise<Array>} Array of companies
 */
export const getUserCompanies = async (userId) => {
    try {
        const isSuperAdmin = await checkSuperAdmin(userId);
        
        if (isSuperAdmin) {
            const { data, error } = await supabase
                .from('equity_companies')
                .select('*')
                .order('created_at', { ascending: false });
            
            if (error) throw error;
            return data || [];
        }

        const { data, error } = await supabase
            .from('equity_users')
            .select(`
                *,
                company:companies(*)
            `)
            .eq('user_id', userId);
        
        if (error) throw error;
        return data?.map(item => item.company).filter(Boolean) || [];
    } catch (error) {
        console.error('Error getting user companies:', error);
        return [];
    }
};

/**
 * Assign user to company
 * @param {string} userId - User ID
 * @param {string} companyId - Company ID
 * @param {string} role - Role to assign
 * @param {string} invitedBy - User ID who invited
 * @returns {Promise<Object>} Result object
 */
export const assignEquityRole = async (userId, companyId, role, invitedBy = null) => {
    try {
        const { data: existingUser } = await supabase
            .from('equity_users')
            .select('id')
            .eq('user_id', userId)
            .eq('company_id', companyId)
            .single();

        if (existingUser) {
            // Update existing role
            const { data, error } = await supabase
                .from('equity_users')
                .update({ role, status: 'active' })
                .eq('user_id', userId)
                .eq('company_id', companyId);
            
            if (error) throw error;
            return { success: true, data };
        } else {
            // Create new role assignment
            const { data, error } = await supabase
                .from('equity_users')
                .insert({
                    user_id: userId,
                    company_id: companyId,
                    role,
                    invited_by: invitedBy,
                    status: 'active'
                });
            
            if (error) throw error;
            return { success: true, data };
        }
    } catch (error) {
        console.error('Error assigning equity role:', error);
        return { success: false, error: error.message };
    }
};

/**
 * Get all users for a company
 * @param {string} companyId - Company ID
 * @returns {Promise<Array>} Array of users with roles
 */
export const getCompanyUsers = async (companyId) => {
    try {
        const { data, error } = await supabase
            .from('equity_users')
            .select(`
                *,
                user:user_profiles(*)
            `)
            .eq('company_id', companyId)
            .order('invited_at', { ascending: false });
        
        if (error) throw error;
        return data || [];
    } catch (error) {
        console.error('Error getting company users:', error);
        return [];
    }
};

/**
 * Remove user from company
 * @param {string} userId - User ID
 * @param {string} companyId - Company ID
 * @returns {Promise<Object>} Result object
 */
export const removeEquityUser = async (userId, companyId) => {
    try {
        const { error } = await supabase
            .from('equity_users')
            .delete()
            .eq('user_id', userId)
            .eq('company_id', companyId);
        
        if (error) throw error;
        return { success: true };
    } catch (error) {
        console.error('Error removing equity user:', error);
        return { success: false, error: error.message };
    }
};
